import { useState } from 'react';
import {
  getScoreHistory,
  SCORE_HISTORY_RANGES,
  type ScoreHistoryRange,
  type ScoreHistoryRead,
} from '../api/endpoints.js';
import { useApi } from '../api/hooks.js';
import { signedDelta } from '../flow/protection.js';
import { Async, Badge, Button, Empty, Note, Panel, Raw, Screen } from '../ui/kit.js';

/**
 * Score history: the number over time, and a sentence for every movement.
 *
 * THIS SCREEN AUTHORS NO COPY AND DOES NO ARITHMETIC. `band` arrives labelled,
 * `change` arrives computed, and each cause arrives with the sentence the api
 * wrote (src/score/reasons.ts). The two rules that copy obeys — never assert a
 * result IS a match, never put a minus sign on a subject's answer — are
 * enforced server-side, and a client that paraphrased would re-open both.
 */
const VIEW_WIDTH = 640;
const VIEW_HEIGHT = 160;
const AXIS_MAX = 100;

/** An inline polyline over the api's own points. No charting library: the
 *  series is a handful of points, and the y positions are a pixel mapping of a
 *  number the server computed — not a recomputation of it. The axis is pinned
 *  0–100 so two windows are comparable at a glance. */
function Line({ points }: { points: ScoreHistoryRead['points'] }) {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) return null;
  const start = new Date(first.at).getTime();
  const span = Math.max(1, new Date(last.at).getTime() - start);
  const path = points
    .map((point) => {
      const x = ((new Date(point.at).getTime() - start) / span) * VIEW_WIDTH;
      const y = VIEW_HEIGHT - (point.live / AXIS_MAX) * VIEW_HEIGHT;
      return `${String(Math.round(x))},${String(Math.round(y))}`;
    })
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
      role="img"
      aria-label="score over time"
      style={{ width: '100%', height: 'auto' }}
    >
      <polyline points={path} fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function Movement({ movement }: { movement: ScoreHistoryRead['movements'][number] }) {
  return (
    <div>
      <p>
        <strong>{signedDelta(movement.delta)}</strong> {new Date(movement.at).toLocaleDateString()}
      </p>
      <ul>
        {movement.causes.map((cause, index) => (
          <li key={`${movement.movement_id}-${String(index)}`}>
            <strong>{signedDelta(cause.delta)}</strong> {cause.title}
            {cause.body === null ? null : <p>{cause.body}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ScoreHistoryScreen() {
  const [range, setRange] = useState<ScoreHistoryRange>('1m');
  const history = useApi(() => getScoreHistory(range), [range]);

  return (
    <Screen
      title="Score history"
      lede="The chart and every sentence beside it come from GET /v1/me/score/history. This screen renders them verbatim — it composes no copy and does no arithmetic on the score. A movement is one recompute; its causes are what moved it, and they sum to it."
    >
      <Panel title="range">
        {SCORE_HISTORY_RANGES.map((option) => (
          <Button
            key={option}
            onClick={() => {
              setRange(option);
            }}
            disabled={option === range}
          >
            {option.toUpperCase()}
          </Button>
        ))}
      </Panel>

      {history.error !== null && history.error.code === 'NO_QUIZ_RESPONSE' ? (
        <Empty>no quiz response yet — take the quiz and a score history begins</Empty>
      ) : history.error !== null && history.error.code === 'QUIZ_OUTDATED' ? (
        <Empty>quiz taken against a retired version — take it again</Empty>
      ) : (
        <Async state={history}>
          {(read) => (
            <>
              <Panel title="your score history">
                <p>
                  <strong>{read.current.live}</strong> <Badge>{read.current.band}</Badge>{' '}
                  <span>{signedDelta(read.change)} over this window</span>
                </p>
                <Line points={read.points} />
                {read.covers_from > read.from ? (
                  <Note>
                    history held from {new Date(read.covers_from).toLocaleDateString()} — earlier
                    than that is not flat, it is unknown
                  </Note>
                ) : null}
              </Panel>

              <Panel title="what moved it">
                {read.movements.length === 0 ? (
                  <Empty>nothing moved your score in this window</Empty>
                ) : (
                  read.movements.map((movement) => (
                    <Movement key={movement.movement_id} movement={movement} />
                  ))
                )}
                {read.movements_truncated ? (
                  <Note>older movements in this window are on GET /v1/me/score/events</Note>
                ) : null}
              </Panel>

              <Note>{read.scope_note}</Note>
              <Raw label="GET /v1/me/score/history" value={read} />
            </>
          )}
        </Async>
      )}
    </Screen>
  );
}
